<template>
    <div>
        <v-data-table
            v-bind="$attrs"
            :headers="headers"
            :loading="loading"
            :items="items"
            :options="tableOptions"
            :search="query"
            fixed-header
            dense
            @update:options="onOptionsUpdate"
            v-on="$listeners"
        >
            <template #top="{ pagination, options, updateOptions }">
                <LightToolbar v-if="!hideTopToolbar" dense>
                    <div>
                        <v-toolbar-title>{{ maintitle }}</v-toolbar-title>
                        <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
                    </div>
                    <slot name="light-toolbar-left" v-bind="{ pagination, options, updateOptions }"></slot>
                    <v-spacer />
                    <slot name="light-toolbar-right" v-bind="{ pagination, options, updateOptions }"></slot>
                </LightToolbar>

                <v-toolbar v-if="!hideToolbar" dense class="elevation-0 base-list-toolbar" :color="color">
                    <slot name="toolbar-start"></slot>
                    <v-btn
                        v-if="newBtn"
                        color="primary white--text"
                        depressed
                        :disabled="disabled"
                        @click="$emit('new-item')"
                    >
                        <KpIcon icon-cls="new" color="white" disabled-color="grey" left />
                        {{ $t('neu') }}
                    </v-btn>
                    <span v-if="newBtn">|</span>
                    <IconBtn
                        v-if="reloadBtn"
                        color="primary"
                        :disabled="disabled"
                        icon-cls="refresh"
                        icon-color="white"
                        icon-disabled-color="grey"
                        :tooltip="$t('reload')"
                        @click="$emit('reload')"
                    />
                    <IconBtn
                        v-if="csvExport"
                        color="primary"
                        :disabled="disabled"
                        icon-cls="download"
                        icon-color="white"
                        icon-disabled-color="grey"
                        :tooltip="$t('csv_export')"
                        @click="$emit('csv-export')"
                    />
                    <IconBtn
                        v-if="xlsxExport"
                        color="primary"
                        :disabled="disabled"
                        icon-cls="download"
                        icon-color="white"
                        icon-disabled-color="grey"
                        :tooltip="$t('xlsx_export')"
                        @click="$emit('xlsx-export')"
                    />
                    <slot name="toolbar-items"></slot>
                    <v-spacer />
                    <slot name="before-query-field" v-bind="{ pagination, options, updateOptions }"></slot>
                    <slot
                        v-if="queryField"
                        :disabled="disabled"
                        name="query-field"
                        v-bind="{ pagination, options, updateOptions }"
                    >
                        <QueryField v-model.lazy="query" @keydown.stop="search" @click:clear="clearSearch" />
                    </slot>
                    <slot name="after-query-field" v-bind="{ pagination, options, updateOptions }"></slot>
                </v-toolbar>
            </template>

            <template
                v-for="field in headers.filter((i) => i.value && i.value !== 'actions')"
                #[createSlotName(field.value)]="data"
            >
                <slot :name="createSlotName(field.value)" v-bind="data">{{ data.value }}</slot>
            </template>
            <template v-if="!showSelectAll" #header.data-table-select>
                <div></div>
            </template>

            <template #expanded-item="data">
                <slot name="expanded-item" v-bind="data"></slot>
            </template>

            <template #[`item.actions`]="{ item }">
                <icon-btn
                    v-if="editBtn"
                    icon-cls="pencil"
                    icon-color="grey"
                    :tooltip="$t('edit')"
                    @click="$emit('edit-item', item)"
                />
                <icon-btn
                    v-if="deleteBtn"
                    icon-cls="trash"
                    icon-color="grey"
                    :tooltip="$t('delete')"
                    @click="itemToDelete = item"
                />
                <slot name="additional-actions" :item="item"> </slot>
            </template>
        </v-data-table>
        <ConfirmDialog
            :value="confirm"
            :context="itemToDelete ? String(itemToDelete.id) : null"
            :title="confirmDialogTitle"
            :message="confirmDialogMessage"
            @confirm="deleteItem"
            @cancel="itemToDelete = null"
        />
    </div>
</template>

<script>
import sessionStorageMixin from '@/mixins/session-storage';
import { getI18nInstance as i18n } from '@/i18n';

/**
 * Base list: Acts as a generic base list to display a grid with
 * basic functionality like querying, sorting, deleting, editing.
 *
 * The component DOES load and manage the collection by itself
 *
 * Slots:
 * - light-toolbar-before-query
 * - query-field (defaults to query field)
 * - light-toolbar-after-query
 * - toolbar-items
 * - additional-actions
 *
 * Events:
 * - update:options: If the table options changed. can be used with options.sync="".
 * - delete-item: if the delete action btn is clicked on an item, delete-item with the model clicked is called, AFTER the user confirmed with yes
 * - new-item: if the new action btn is clicked
 * - reload: if the reload btn is clicked
 * - edit-item: if the edit action btn is clicked on an item, edit-item with the model clicked is called
 */
export default {
    name: 'BaseList',
    mixins: [sessionStorageMixin],

    props: {
        items: {
            type: Array,
            default: () => [],
        },
        // needed for sessionSet/Get to identify the component uniquely. Provide something like "user-list".
        sessionKey: {
            type: String,
            required: true,
        },
        hideTopToolbar: {
            type: Boolean,
            default: false,
        },
        hideToolbar: {
            type: Boolean,
            default: false,
        },
        // the displayed title
        maintitle: {
            type: String,
            default: '',
        },

        // true to show a new button. it emits a 'new-item' event if clicked.
        newBtn: {
            type: Boolean,
            default: true,
        },

        // true to show a reload button. it emits a 'reload' event if clicked.
        reloadBtn: {
            type: Boolean,
            default: true,
        },

        // true to show a reload button. it emits a 'edit-item' event if clicked.
        editBtn: {
            type: Boolean,
            default: true,
        },

        // true to show a delete button. it shows a confirm dialog.
        deleteBtn: {
            type: Boolean,
            default: true,
        },

        confirmDialogTitle: {
            type: String,
            default: () => i18n().t('rec_delete'),
        },

        confirmDialogMessage: {
            type: String,
            default: () => i18n().t('confirm_rec_delete'),
        },

        // true to show a csv download btn. it emits a 'csv-export' event if clicked.
        csvExport: {
            default: false,
            type: Boolean,
        },

        // true to show a xlsx download btn. it emits a 'xlsx-export' event if clicked.
        xlsxExport: {
            default: false,
            type: Boolean,
        },

        // true to show a search query field, which binds to the query data prop
        queryField: {
            type: Boolean,
            default: true,
        },

        // the data table's headers
        headers: {
            type: Array,
            required: true,
        },
        loading: Boolean,
        disabled: Boolean,
        options: {
            type: Object,
            default: () => {
                return {
                    page: 1,
                    itemsPerPage: 50,
                };
            },
        },
        subtitle: {
            type: String,
            default: '',
        },
        showSelectAll: {
            type: Boolean,
            default: true,
        },
        color: {
            type: String,
            default: 'primary white--text',
        },
    },

    data() {
        return {
            itemToDelete: null,
            total: 0,
            query: this.getSessionValue('query', ''),
        };
    },

    computed: {
        // table options contain the options from the parent prop
        // and enriches them with the session-stored values:
        tableOptions() {
            return {
                ...this.options,
                ...this.getSessionValue('tableOptions'),
            };
        },
        confirm() {
            return this.itemToDelete !== null;
        },
    },

    methods: {
        onOptionsUpdate() {
            this.setSessionValue('tableOptions', { ...this.tableOptions });
            this.$emit('update:options', { ...this.tableOptions });
        },

        initiateDelete(record) {
            this.itemToDelete = record;
        },

        createSlotName(value) {
            return `item.${value}`;
        },

        search() {
            this.setSessionValue('query', this.query);
        },

        deleteItem() {
            let item = this.itemToDelete;
            this.$emit('delete-item', item);
            this.itemToDelete = null;
        },

        clearSearch() {
            this.query = '';
            this.clearSessionValue('query');
        },
    },
};
</script>

<style lang="scss" scoped>
.subtitle {
    font-size: 1rem;
    color: var(--gray-text-color);
}
</style>
